import { faAnglesLeft, faAnglesRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Input, Loading, Modal } from '@orobix/orobix-fe-components'
import useSize from '@react-hook/size'
import { useQuery } from '@tanstack/react-query'
import { FormikHelpers } from 'formik'
import { isNil } from 'ramda'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Observation } from '../../../types'
import { ObservationCompact } from '../../../types/observation'
import { get } from '../../../utils/api'
import useStyles from './PatchesConfigurationModal.styles'
import { getTileX, getTileY, hasNextItem, hasPrevItem } from './utils'

interface PatchesConfigurationModalProps<FormValues> {
  selectedObservations: ObservationCompact[]
  initialValues: {
    horizontalSplit?: number
    verticalSplit?: number
    overlap?: number
  }
  setModal: React.Dispatch<React.SetStateAction<boolean>>
  setFieldValue: FormikHelpers<FormValues>['setFieldValue']
}

const PatchesConfigurationModal = <FormValues,>({
  selectedObservations,
  initialValues,
  setModal,
  setFieldValue,
}: PatchesConfigurationModalProps<FormValues>) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [horizontalSplit, setHorizontalSplit] = useState(
    initialValues.horizontalSplit || 2
  )
  const [verticalSplit, setVerticalSplit] = useState(
    initialValues.verticalSplit || 2
  )
  const [
    currentObservationIndex,
    setCurrentObservationIndex,
  ] = useState<number>(0)
  const [overlap, setOverlap] = useState(initialValues.overlap || 0)
  const tiles = Array.from({ length: horizontalSplit * verticalSplit })
  const imageRef = useRef<HTMLImageElement>(null)
  const imageWrapperRef = useRef<HTMLDivElement>(null)
  const [imageWrapperWidth] = useSize(imageWrapperRef)
  const [imageSize, setImageSize] = useState<{
    width: number
    height: number
  }>()
  const currentObservationId =
    currentObservationIndex < selectedObservations.length
      ? selectedObservations[currentObservationIndex].id
      : undefined
  const { data: currentObservation, isLoading } = useQuery(
    ['observations', currentObservationId],
    () => get<Observation>(`/observations/${currentObservationId}`),
    { retry: false, enabled: !isNil(currentObservationId) }
  )

  const handleHorizontalSplit = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    if (value === 1 && verticalSplit === 1) setVerticalSplit(2)

    setHorizontalSplit(value)
  }

  const handleVerticalSplit = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    if (value === 1 && horizontalSplit === 1) setHorizontalSplit(2)

    setVerticalSplit(value)
  }

  const imageBoundingBox = { width: imageWrapperWidth, height: 500 }

  useEffect(() => {
    if (!currentObservation) return

    setImageSize(undefined)

    const image = currentObservation.large

    const imageBoundingBoxRatio =
      imageBoundingBox.width / imageBoundingBox.height

    if (imageWrapperWidth > 0 && image) {
      const img = new Image()
      img.src = image
      img.onload = () => {
        const imageRatio = img.width / img.height

        if (imageRatio >= imageBoundingBoxRatio) {
          setImageSize({
            width: imageBoundingBox.width,
            height: imageBoundingBox.width / imageRatio,
          })
        } else {
          setImageSize({
            width: imageBoundingBox.height * imageRatio,
            height: imageBoundingBox.height,
          })
        }
      }
    }
  }, [
    currentObservation,
    imageBoundingBox.height,
    imageBoundingBox.width,
    imageWrapperWidth,
    selectedObservations,
  ])

  return (
    <Modal
      size="xl"
      onCloseRequest={() => setModal(false)}
      title={t('Configure patches')}
    >
      <>
        <div className={classes.imageNavigation}>
          <Button
            onClick={() =>
              hasPrevItem(currentObservationIndex) &&
              setCurrentObservationIndex(currentObservationIndex - 1)
            }
            label={t('Previous image')}
            type="default"
            buttonType="button"
            size="sm"
            icon={<FontAwesomeIcon icon={faAnglesLeft} />}
            disabled={!hasPrevItem(currentObservationIndex)}
          />
          <Button
            onClick={() =>
              hasNextItem(
                currentObservationIndex,
                selectedObservations.length
              ) && setCurrentObservationIndex(currentObservationIndex + 1)
            }
            label={t('Next image')}
            type="default"
            buttonType="button"
            size="sm"
            icon={<FontAwesomeIcon icon={faAnglesRight} />}
            disabled={
              !hasNextItem(currentObservationIndex, selectedObservations.length)
            }
          />
        </div>
        <div className={classes.wrapper}>
          <div
            className={classes.imageWrapper}
            ref={imageWrapperRef}
            style={{ height: imageBoundingBox.height }}
          >
            {isLoading && (
              <div
                className={classes.loading}
                style={{
                  top: imageBoundingBox.height / 2 - 12, // 12 = loading spinner height
                  left: imageBoundingBox.width / 2 - 8, // 8 = loading spinner width
                }}
              >
                <Loading />
              </div>
            )}

            {imageSize && currentObservation && (
              <>
                <img
                  style={{
                    width: imageSize.width,
                    height: imageSize.height,
                  }}
                  className={classes.image}
                  ref={imageRef}
                  src={currentObservation.large}
                  alt=""
                />

                <div
                  className={classes.tiles}
                  style={{
                    width: imageSize.width,
                    height: imageSize.height,
                  }}
                >
                  {tiles.map((tile, i) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className={classes.tile}
                      style={{
                        width: `${100 / horizontalSplit}%`,
                        height: `${100 / verticalSplit}%`,
                        top: `${getTileX(i, horizontalSplit, verticalSplit)}%`,
                        left: `${getTileY(i, horizontalSplit)}%`,
                      }}
                    >
                      <div
                        className={classes.tileContent}
                        style={{
                          top: `-${overlap}%`,
                          right: `-${overlap}%`,
                          bottom: `-${overlap}%`,
                          left: `-${overlap}%`,
                        }}
                      />
                    </div>
                  ))}

                  {tiles.map((tile, i) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className={classes.tileBorder}
                      style={{
                        width: `${100 / horizontalSplit}%`,
                        height: `${100 / verticalSplit}%`,
                        top: `${getTileX(i, horizontalSplit, verticalSplit)}%`,
                        left: `${getTileY(i, horizontalSplit)}%`,
                      }}
                    >
                      <div className={classes.tileBorderContent}>{i + 1}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className={classes.sliders}>
            <Input
              name="horizontalSplit"
              id="horizontalSplit"
              label={t('Horizontal splitting')}
              type="range"
              min="1"
              max="15"
              step="1"
              rangeShowValue
              onChange={handleHorizontalSplit}
              value={horizontalSplit}
            />

            <Input
              name="verticalSplit"
              id="verticalSplit"
              label={t('Vertical splitting')}
              type="range"
              min="1"
              max="15"
              step="1"
              rangeShowValue
              onChange={handleVerticalSplit}
              value={verticalSplit}
            />

            <Input
              name="overlap"
              id="overlap"
              label={t('Overlap')}
              type="range"
              min="0"
              max="80"
              step="1"
              rangeShowValue
              unit="%"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOverlap(Number(e.target.value))
              }
              value={overlap}
            />

            <div className={classes.buttons}>
              <Button
                onClick={() => setModal(false)}
                label={t('Cancel')}
                type="default"
                buttonType="button"
              />

              <Button
                label={t('Confirm')}
                onClick={() => {
                  setModal(false)
                  setFieldValue('horizontalPatches', horizontalSplit)
                  setFieldValue('verticalPatches', verticalSplit)
                  setFieldValue('patchesOverlap', overlap)
                }}
                type="action"
                buttonType="button"
              />
            </div>
          </div>
        </div>
      </>
    </Modal>
  )
}

export default PatchesConfigurationModal
